import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Pagination,
  Skeleton,
  Snackbar,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
  TextField
} from '@mui/material'
import { Helmet } from 'react-helmet-async'
import MainCard, {
  ErrorScreen,
  LoadingDialog,
  ShowNoRecordsFound,
  SnackBarHideDuration,
  SnackBarTransition,
  StyledTableCell,
  StyledTableRow
} from '../../utils/ui_utils'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { businessCheck, isEmpty } from '../../utils/utils'
import { Download } from 'react-feather'
import * as XLSX from 'xlsx'
import { CheckCircleOutline } from '@mui/icons-material'
import ExportDialog from '../../utils/ui_components/exportDailogBox'
import { OutletAndDatePickerUI } from '../../utils/ui_components/outletAndDatePickerUi'
import {
  useDownloadReports,
  useFetchInventoryReport
} from '../../modules/reports/reportsHooks'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import { useDispatch } from 'react-redux'
import { downloadReportsSlice } from '../../modules/reports/reportsSlice'
import { type } from '@testing-library/user-event/dist/cjs/utility/type.js'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ReportRefreshControls from '../../utils/ui_components/ReportRefreshControls'
import useReportRefresh from '../../hooks/useReportRefresh'
import { generateXLSXCSV } from '../../utils/tsUtils/utils'
import { getUserType } from '../../components/tsComponents/AuthGuard'

const InventoryReport = () => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [pagecount, setPageCount] = useState(1)
  let [exportPage, setExportPage] = useState(1)
  const [exportLimit, setExportLimit] = useState(50)
  const [selectedOutlet, setSelectedOutlet] = useState('')
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)
  const [fromDate, setFromDate] = useState(moment().startOf('day'))
  const [isExport, setIsExport] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [outlets, setOutlets] = useState([])
  const [selectedOutletName, setSelectedOutletName] = useState('')
  const [reportVisibility, setReportVisibility] = useState(false)
  const [reportArray, setReportArray] = useState([])
  const [exportDailogVisibility, setExportDailogVisibility] = useState(false)
  const [exportMsg, setExportMsg] = useState('Inventory report exporting...')
  const [exportIcon, setExportIcon] = useState(<CircularProgress size={20} />)
  const [okButtonStatus, setOkButtonStatus] = useState(true)
  const [downloadStatus, setDownloadStatus] = useState(false)
  const [goToPageInput, setGoToPageInput] = useState('')
  const {
    inventoryReportData,
    inventoryReportError,
    inventoryReportFetch,
    inventoryReportLoading
  } = useFetchInventoryReport()
  const { outletFetch, outletFetchData, outletFetchError, outletFetchLoading } =
    useFetchOutlet()

  const { downloadReportsLoading, downloadReportsFetch } = useDownloadReports()

  // Determine user type and build outlets with Owner-only All Outlets (id '0')
  const userType = getUserType()
  const isOwner = userType === 'Owner'
  const baseOutlets = Array.isArray(outletFetchData?.data)
    ? outletFetchData.data.filter(
        (o) => String(o?._id) !== '0' && o?.name !== 'All Outlets'
      )
    : []
  const outletsWithAll = isOwner
    ? [{ _id: '0', name: 'All Outlets' }, ...baseOutlets]
    : baseOutlets

  // Helper: treat '0' as All Outlets only for Owner
  const isAllOutletsSelected = isOwner && selectedOutlet === '0'

  let date = new Date()
  let dateStr = date.toISOString().slice(0, 10)

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const validateAndGetReport = (page) => {
    // Allow reports to be fetched regardless of outlet selection (Owner may choose All)
    fetchRecord(page)
  }

  const fetchRecord = (page) => {
    let payload = {
      report_date: moment(fromDate).format('YYYY-MM-DD'),
      pageNumber: page,
      limit: limit
    }

    // Include outlet_id only when not Owner selecting All
    if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
      payload.outlet_id = selectedOutlet
    }

    inventoryReportFetch(payload)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const handlePageChange = async (event, value) => {
    setPage(value)
    fetchRecord(value)
  }

  const pageCount = (data1) => {
    let count = Math.ceil(data1 / limit)
    setPageCount(count)
  }

  const history = useNavigate()
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(downloadReportsSlice.actions.reset())
    businessCheck(history)
    let payload = {
      pageNumber: 1,
      limit: 100
    }
    outletFetch(payload)
  }, [])

  useEffect(() => {
    // Default outlet selection based on role after outlets are fetched
    setOutlets(baseOutlets)
    if (isOwner) {
      setSelectedOutlet('0')
    } else if (
      Array.isArray(outletFetchData?.data) &&
      outletFetchData.data.length > 0
    ) {
      setSelectedOutlet(String(outletFetchData.data[0]._id))
    } else {
      setSelectedOutlet('')
    }
  }, [outletFetchData, isOwner])

  useEffect(() => {
    if (inventoryReportData.data?.length > 0) {
      pageCount(inventoryReportData.total)
      setReportVisibility(true)
    } else {
      setReportVisibility(false)
    }
  }, [inventoryReportData])

  // useEffect(() => {
  //     if (ReportsAPI.fetchExportInventoryReport.state.totalRecords != undefined) {
  //         if (Object.keys(ReportsAPI.fetchExportInventoryReport.state.data).length > 0) {
  //             const json = ReportsAPI.fetchExportInventoryReport.state.data
  //             const refinedJson = json.map((item) => {
  //                 return {
  //                     "Name": item.item_name,
  //                     "Opening Stock": item.opening_stock,
  //                     "Purchase Stock": item.purchase_stock,
  //                     "Sold Stock": item.sold_stock,
  //                     "Closing Stock": item.closing_stock,
  //                 }
  //             })
  //             setReportArray(prevArray => [...prevArray, ...refinedJson])
  //         }
  //     }
  // }, [ReportsAPI.fetchExportInventoryReport.state])

  // useEffect(() => {
  //     if (reportArray.length > 0) {
  //         let nextPage = exportPage + 1;
  //         let total_Pages = Math.ceil(ReportsAPI.fetchExportInventoryReport.state.totalRecords / exportLimit)
  //         if (total_Pages < nextPage && !downloadStatus) {
  //             const worksheet = XLSX.utils.json_to_sheet(reportArray);
  //             const workbook = XLSX.utils.book_new();
  //             XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  //             XLSX.writeFile(workbook, `Invertory_report-${selectedOutletName}-${dateStr}.csv`, { compression: true });
  //             setExportMsg("Inventory report exported successfully")
  //             setExportIcon(<CheckCircleOutline color='primary' />)
  //             setOkButtonStatus(false)
  //             setDownloadStatus(true)
  //         }
  //         if (total_Pages >= nextPage) {
  //             setExportPage(nextPage)
  //             ReportsAPI.fetchExportInventoryReport.api(moment(fromDate).format("YYYY-MM-DD"), selectedOutlet, nextPage, exportLimit)
  //         }
  //     }
  // }, [reportArray])

  const handleOutletChange = (event) => {
    setSelectedOutlet(event.target.value)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNumber = parseInt(goToPageInput, 10)
    if (pageNumber >= 1 && pageNumber <= pagecount) {
      handlePageChange(null, pageNumber)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const refreshFunction = () => validateAndGetReport(page)
  const { autoRefresh, handleToggleAutoRefresh, handleManualRefresh } =
    useReportRefresh(refreshFunction, [page, selectedOutlet, fromDate])

  const fetchOutletOnError = () => {
    let payload = {
      pageNumber: 1,
      limit: 100
    }
    outletFetch(payload)
  }

  const fetchInventoryReportOnError = () => {
    let payload = {
      report_date: moment(fromDate).format('YYYY-MM-DD'),
      outlet_id: selectedOutlet,
      pageNumber: page,
      limit: limit
    }
    inventoryReportFetch(payload)
  }

  useEffect(() => {
    setPage(1)
    validateAndGetReport(1)
  }, [selectedOutlet, fromDate])

  const exportToCsv = async () => {
    try {
      let payload = {
        report_date: moment(fromDate).format('YYYY-MM-DD'),
        pageNumber: 1,
        limit: 100,
        total: inventoryReportData?.total || 15,
        type: 'inventory'
      }

      // Include outlet_id only when not Owner selecting All
      if (!isAllOutletsSelected && !isEmpty(selectedOutlet)) {
        payload.outlet_id = selectedOutlet
      }

      const reports = await downloadReportsFetch(payload)

      let data1 = [...reports.payload]

      const options = {
        keys: [
          { field: 'item_name', title: 'Name' },
          { field: 'opening_stock', title: 'Opening Stock' },
          { field: 'purchase_stock', title: 'Purchase Stock' },
          { field: 'sold_stock', title: 'Sold Stocks' },
          { field: 'closing_stock', title: 'Closing Stock' }
        ]
      }

      let outletName = 'All_Outlets'
      if (!isAllOutletsSelected) {
        const selectedOutletData = outletFetchData.data.find(
          (outlet) => String(outlet._id) === String(selectedOutlet)
        )
        outletName = selectedOutletData?.name || 'Unknown_Outlet'
      }

      let filename = `Inventory_Report_${outletName}_`
      filename += `till_${payload.report_date}`

      const csv = await generateXLSXCSV(data1, options.keys, filename)

      toastMessage('CSV Downloaded Sucessfully')
    } catch (error) {
      console.error('Error while converting JSON to CSV', error)
      toastMessage('Error While Downloading csv', 'error')
    }
  }

  return (
    <>
      <Helmet title="Inventory Report" />
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Grid item>
          <Typography variant="h4" gutterBottom display="inline">
            Inventory Report
          </Typography>
        </Grid>
        <Grid item>
          <ReportRefreshControls
            autoRefresh={autoRefresh}
            onToggleAutoRefresh={handleToggleAutoRefresh}
            onManualRefresh={handleManualRefresh}
            isLoading={inventoryReportLoading}
          />
        </Grid>
      </Grid>
      <Divider sx={{ marginTop: 2 }} />
      {outletFetchError ? (
        <ErrorScreen
          onClick={() => {
            fetchOutletOnError()
          }}
        />
      ) : (
        <div>
          {
            <OutletAndDatePickerUI
              outlets={outletsWithAll}
              onOutletChange={handleOutletChange}
              selectedOutlet={selectedOutlet}
              fromDate={fromDate}
              fromDateLabel="Report Date"
              onFromDateChange={(newValue) => {
                newValue.setHours(0, 0, 0)
                setFromDate(newValue)
              }}
              onFromDateError={(reason, value) => {
                if (reason) {
                  setErrorMessage(reason)
                  setShowError(true)
                } else {
                  setErrorMessage('')
                  setShowError(false)
                }
              }}
              showFromDateError={showError}
              fromDateErrorMessage={errorMessage}
              exportComponent={
                inventoryReportData.data &&
                inventoryReportData.data.length > 0 && (
                  <Tooltip title="Export to CSV" placement="top" arrow>
                    <IconButton
                      variant="contained"
                      onClick={exportToCsv}
                      disabled={downloadReportsLoading}
                      size="large"
                    >
                      {downloadReportsLoading && <CircularProgress size={20} />}
                      {!downloadReportsLoading ? (
                        <>
                          {' '}
                          <CloudUploadIcon fontSize="large" />
                        </>
                      ) : (
                        <> </>
                      )}
                    </IconButton>
                  </Tooltip>
                )
              }
            />
          }
          {inventoryReportError ? (
            <ErrorScreen
              onClick={() => {
                fetchInventoryReportOnError()
              }}
            />
          ) : (
            <div>
              {(inventoryReportLoading == true ||
                inventoryReportLoading == null ||
                outletFetchLoading) && (
                <Grid
                  container
                  spacing={10}
                  direction={'column'}
                  sx={{ paddingTop: 5 }}
                >
                  <Grid item>
                    <Grid
                      container
                      rowSpacing={4.5}
                      columnSpacing={10}
                      sx={{ mt: 1 }}
                    >
                      <Grid item xs sm md={8}>
                        <MainCard content={false} sx={{ mt: 1.5 }}>
                          <Box sx={{ pt: 1, pr: 2, pl: 2, pb: 1 }}>
                            <Grid container sx={{ ml: 5 }}>
                              <Grid item xs sm md>
                                <Grid container direction="column">
                                  <Typography
                                    variant="h4"
                                    justifyContent={'center'}
                                    sx={{ mb: 0.5 }}
                                  >
                                    <Skeleton
                                      animation="wave"
                                      variant="rectangular"
                                      sx={{
                                        width: { xs: 50, sm: 50, md: 500 },
                                        height: 50
                                      }}
                                    />
                                  </Typography>
                                  <Grid
                                    container
                                    justifyContent="center"
                                    sx={{ paddingTop: 5, ml: 8 }}
                                  >
                                    <Grid item sm md>
                                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                                        <Skeleton
                                          animation="wave"
                                          variant="rounded"
                                          width={200}
                                          height={25}
                                        />
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid
                                    container
                                    justifyContent="space-arround"
                                    sx={{ paddingTop: 3, ml: 8 }}
                                  >
                                    <Grid item sm={3} md={3}>
                                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                                        <Skeleton
                                          animation="wave"
                                          variant="rounded"
                                          width={200}
                                          height={25}
                                        />
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid
                                    container
                                    justifyContent="space-arround"
                                    sx={{ paddingTop: 3, ml: 8 }}
                                  >
                                    <Grid item sm={3} md={3}>
                                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                                        <Skeleton
                                          animation="wave"
                                          variant="rounded"
                                          width={200}
                                          height={25}
                                        />
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid
                                    container
                                    justifyContent="space-arround"
                                    sx={{ paddingTop: 3, ml: 8 }}
                                  >
                                    <Grid item sm={3} md={3}>
                                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                                        <Skeleton
                                          animation="wave"
                                          variant="rounded"
                                          width={200}
                                          height={25}
                                        />
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  <Grid
                                    container
                                    justifyContent="space-arround"
                                    sx={{ paddingTop: 3, ml: 8, mb: 5 }}
                                  >
                                    <Grid item sm md>
                                      <Typography variant="h6" sx={{ mb: 0.5 }}>
                                        <Skeleton
                                          animation="wave"
                                          variant="rounded"
                                          width={200}
                                          height={25}
                                        />
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Box>
                        </MainCard>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {!inventoryReportLoading &&
                !inventoryReportError &&
                !outletFetchLoading &&
                !outletFetchError &&
                reportVisibility && (
                  <Grid
                    container
                    spacing={10}
                    direction={'column'}
                    sx={{ paddingTop: 10 }}
                  >
                    <Grid item>
                      <Grid container spacing={10}>
                        <Grid item xs sm md={10}>
                          <MainCard
                            content={false}
                            sx={{
                              width: '100%',
                              maxWidth: 'calc(100vw - 300px)', // Account for sidebar width
                              '@media (max-width: 960px)': {
                                maxWidth: '100vw' // Full width on mobile when sidebar is collapsed
                              }
                            }}
                          >
                            <CardContent sx={{ flexGrow: 1 }}>
                              <TableContainer
                                component={Card}
                                sx={{
                                  width: '100%',
                                  maxWidth: '100%',
                                  overflow: 'auto'
                                }}
                              >
                                <Table size="small">
                                  <TableHead size="small">
                                    <StyledTableRow>
                                      <StyledTableCell sx={{ minWidth: 200 }}>
                                        Name
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Opening Stock
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Purchase Stock
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Sold Stock
                                      </StyledTableCell>
                                      <StyledTableCell align="right">
                                        {' '}
                                        Closing Stock
                                      </StyledTableCell>
                                    </StyledTableRow>
                                  </TableHead>
                                  <TableBody>
                                    {inventoryReportData.data?.map((row, i) => (
                                      <StyledTableRow key={i} hover>
                                        <StyledTableCell sx={{ minWidth: 200 }}>
                                          {row.item_name}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.opening_stock.toFixed(3)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.purchase_stock.toFixed(3)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.sold_stock.toFixed(3)}
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                          {row.closing_stock.toFixed(3)}
                                        </StyledTableCell>
                                      </StyledTableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </CardContent>
                            <ExportDialog
                              visible={exportDailogVisibility}
                              title="Export Inventory Report"
                              onClose={() => {
                                setExportDailogVisibility(false)
                              }}
                              exportMsg={exportMsg}
                              exportIcon={exportIcon}
                              okButtonStatus={okButtonStatus}
                              okSubmit={() => {
                                setExportMsg('Inventory report exporting...')
                                setExportIcon(<CircularProgress size={20} />)
                                setReportArray([])
                                setExportDailogVisibility(false)
                                setDownloadStatus(false)
                                setExportPage(1)
                              }}
                            />
                            {pagecount > 1 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 2,
                                  p: 2,
                                  backgroundColor: 'grey.50',
                                  borderTop: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    width: '100%'
                                  }}
                                >
                                  <Pagination
                                    count={pagecount}
                                    page={page}
                                    color="secondary"
                                    size="small"
                                    boundaryCount={2}
                                    siblingCount={0}
                                    showFirstButton
                                    showLastButton
                                    onChange={handlePageChange}
                                    sx={{
                                      '& .MuiPaginationItem-root': {
                                        transition: 'all 0.2s ease-in-out'
                                      }
                                    }}
                                  />
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    backgroundColor: 'white',
                                    p: 2,
                                    borderRadius: 1,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    transition: 'box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                                    }
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 500
                                    }}
                                  >
                                    Jump to
                                  </Typography>
                                  <TextField
                                    size="small"
                                    value={goToPageInput}
                                    onChange={handleGoToPageChange}
                                    onKeyPress={handleGoToPageKeyPress}
                                    placeholder="Page"
                                    sx={{
                                      width: '80px',
                                      '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        transition: 'all 0.2s ease-in-out',
                                        '& fieldset': {
                                          borderColor: 'grey.300',
                                          borderWidth: '1px'
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'secondary.main',
                                          borderWidth: '1.5px'
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: 'secondary.main',
                                          borderWidth: '2px',
                                          boxShadow:
                                            '0 0 0 3px rgba(156, 39, 176, 0.1)'
                                        }
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        textAlign: 'center',
                                        fontWeight: 500,
                                        padding: '7px 8px',
                                        height: '20px',
                                        lineHeight: '20px'
                                      }
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 400
                                    }}
                                  >
                                    of {pagecount}
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    onClick={handleGoToPageSubmit}
                                    disabled={
                                      !goToPageInput ||
                                      parseInt(goToPageInput, 10) < 1 ||
                                      parseInt(goToPageInput, 10) > pagecount
                                    }
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 500,
                                      px: 2,
                                      py: 0.75,
                                      height: '34px',
                                      lineHeight: 1,
                                      transition: 'all 0.2s ease-in-out',
                                      '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow:
                                          '0 4px 8px rgba(156, 39, 176, 0.3)'
                                      },
                                      '&:active': {
                                        transform: 'translateY(0)'
                                      }
                                    }}
                                  >
                                    Go
                                  </Button>
                                </Box>
                              </Box>
                            )}
                            {/* <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 30 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        size="small"
                                        sx={{ m: 3 }}
                                        startIcon={<Download />}
                                        onClick={() => {
                                            setIsExport(true)
                                            setExportDailogVisibility(true)
                                            ReportsAPI.fetchExportInventoryReport.api(moment(fromDate).format("YYYY-MM-DD"), selectedOutlet, exportPage, exportLimit)
                                            for (let i = 0; i < outlets.length; i++) {
                                                if (outlets[i]._id === selectedOutlet) {
                                                    setSelectedOutletName(outlets[i].name);
                                                }
                                            }
                                        }}
                                    >
                                        Export
                                    </Button>
                                </div> */}
                          </MainCard>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              {!inventoryReportLoading &&
                !inventoryReportError &&
                !outletFetchLoading &&
                !outletFetchError &&
                !reportVisibility && <ShowNoRecordsFound />}
            </div>
          )}
        </div>
      )}

      <div>
        <Snackbar
          open={snackbar}
          autoHideDuration={SnackBarHideDuration}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          onClose={handleSnackbarClose}
          TransitionComponent={SnackBarTransition}
        >
          <Alert onClose={handleSnackbarClose} severity={severity}>
            {PopUpMessage}
          </Alert>
        </Snackbar>
      </div>

      <LoadingDialog open={downloadReportsLoading} />
    </>
  )
}

export default InventoryReport
